<template>
  <div class="language-snapshot-card">
    <div class="phrases-item">
      <h3 v-if="!isLoading">
        {{ name }}
      </h3>
      <v-skeleton-loader
        v-else
        type="text"
        width="80px"
      />

      <h4 v-if="!isLoading">
        {{ message }}
      </h4>
      <v-skeleton-loader
        v-else
        class="mt-3"
        type="text"
      />

      <ul
        v-if="!isLoading"
        class="phrases-examples"
        :class="[color || 'grey']"
      >
        <li
          v-for="example in examples"
          :key="example"
        >
          “{{ example }}”
        </li>
      </ul>
      <v-skeleton-loader
        v-else
        class="mt-2"
        type="chip"
      />

      <div class="line" />

      <div class="d-flex align-start">
        <div class="flex-grow-5">
          <p v-if="!isLoading">
            <strong>
              {{ totalJobs }}
            </strong>
            / {{ overallTotalJobs }} Jobs
          </p>
          <v-skeleton-loader
            v-else
            class="mt-1 mb-4"
            type="text"
            width="60px"
          />

          <div class="impact-progress-wrap">
            <div
              class="impact-progress-wrap-in progress-animation-horizontal"
              :class="[color || 'grey']"
              :style="{ width: progressWidth }"
            />
            <div
              class="impact-progress-wrap-in impact-progress-wrap-in-background"
              :class="[color || 'grey']"
            />
          </div>
        </div>

        <div class="flex-grow-7">
          <div class="points-per-row-wrap">
            <p v-if="!isLoading">
              <strong>
                {{ perJob.toFixed(1) }}x
              </strong>
              per job
            </p>
            <v-skeleton-loader
              v-else
              type="text"
              width="60px"
              class="mb-4"
            />

            <div
              class="points-per-row"
              :class="[color || 'grey']"
            >
              <div
                v-for="(_, i) in Array(wholePerJob)"
                :key="i"
                class="point-per-row"
              >
                <div />
              </div>
              <div
                v-if="fractionPerJob"
                class="point-per-row"
              >
                <div :style="{ width: `${(fractionPerJob * 0.5 + 0.25) * 100}%` }" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isLoading || topPlaces.length"
        class="most-often"
      >
        <div v-if="!isLoading">
          Found most often:
        </div>
        <v-skeleton-loader
          v-else
          type="text"
        />

        <div
          v-if="!isLoading"
          class="most-often-results"
        >
          {{ topPlacesText }}
        </div>
        <v-skeleton-loader
          v-else
          type="text"
          height="12"
          class="mt-2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { capitalize } from 'lodash-es';

export default {
  name: 'LanguageSnapshotCard',

  props: {
    isLoading: { type: Boolean, default: false },
    name: { type: String, default: '' },
    message: { type: String, default: '' },
    examples: { type: Array, default: () => [] },
    color: { type: String, default: '' },
    percentage: { type: Number, default: 0 },
    perJob: { type: Number, default: 0 },
    totalJobs: { type: Number, default: 0 },
    topPlaces: { type: Array, default: () => [] },
    overallTotalJobs: { type: Number, default: 0 },
  },

  data() {
    return {
      progressWidth: '0',
    };
  },

  computed: {
    wholePerJob() {
      return Math.floor(this.perJob);
    },
    fractionPerJob() {
      const fraction = this.perJob - this.wholePerJob;
      return fraction > 0.01 ? fraction : 0;
    },
    topPlacesText() {
      const places = this.topPlaces.map(capitalize);

      if (places.length === 1) {
        return `${places[0]} Jobs`;
      }

      const lastPlace = places.pop();
      return `${places.join(', ')} and ${lastPlace} Jobs`;
    },
  },

  watch: {
    isLoading: {
      immediate: true,
      handler(isLoading) {
        if (isLoading) {
          this.progressWidth = '0';
        } else {
          setTimeout(() => {
            this.progressWidth = `${this.percentage * 100}%`;
          }, 100);
        }
      },
    },
  },
};
</script>

<style scoped lang="less">
.language-snapshot-card {
  strong {
    font-weight: 500;
  }
  p {
    margin-bottom: 16px !important;
  }
}

.phrases-item {
  background: @white;
  border: 1px solid @stroke-default;
  border-radius: 8px;
  font-weight: 300;
  font-size: 16px;
  line-height: 20px;
  margin-bottom: 12px;
  position: relative;
  padding: 24px;
  h3, .h3 {
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    padding-bottom: 4px;
  }
  h4, .h4 {
    font-size: 16px;
    margin: 0;
    line-height: 20px;
    font-weight: 300;
    padding: 0 0 12px;
  }
  .impact-progress-wrap {
    background: transparent;
    height: 6px;
    width: 80px;
    margin-bottom: 4px;
    position: relative;
  }
  .impact-progress-wrap-in {
    height: 6px;
    z-index: 2;
    position: relative;
    background: rgba(@grey-900, .1);
    border-radius: 10px;
  }
  .impact-progress-wrap-in-background {
    position: absolute;
    left: 0;
    right: 0;
    height: 6px;
    background: rgba(@grey-900, .1);
    border-radius: 10px;
    z-index: 1;
    top: 0;
    width: auto;
  }
  .line {
    margin: 8px 0 12px;
    height: 1px;
    background: rgba(@grey-900, .05);
  }
}
.phrases-examples {
  font-size: 12px;
  line-height: 16px;
  list-style: none;
  padding: 0;
  display: flex;
  flex-flow: row wrap;
  li {
    padding: 4px 8px;
    background: @bg-dark;
    margin: 0 8px 8px 0;
    &:before, &:after {
      display: none;
    }
  }
  &.score-good {
    li {
      background: rgba(@basic-green, .2);
    }
  }
  &.score-near-good {
    li {
      background: rgba(@blue-300, .2);
    }
  }
  &.score-average {
    li {
      background: rgba(@brand-primary, .2);
    }
  }
  &.score-poor {
    li {
      background: rgba(@basic-red, .2);
    }
  }
  &.score-near-bad {
    li {
      background: rgba(@basic-red, .2);
    }
  }
  &.score-bad {
    li {
      background: rgba(@basic-red, .2);
    }
  }
  &.score-none {
    li {
      background: rgba(@grey-400, .2);
    }
  }

  &.green-light {
    li {
      background: rgba(@green-700, .2);
    }
  }
  &.green, &.green-solid, &.green-underline {
    li {
      background: rgba(#9DE3D1, .5);
    }
  }
  &.green-dark {
    li {
      background: rgba(@basic-green, .2);
    }
  }
  &.blue-light {
    li {
      background: rgba(@blue-300, .2);
    }
  }
  &.blue, &.blue-solid, &.blue-underline {
    li {
      background: rgba(#99d5f1, .5);
    }
  }
  &.blue-dark {
    li {
      background: rgba(@navy-700, .2);
    }
  }
  &.blue-extradark {
    li {
      background: rgba(@grey-900, .2);
    }
  }
  &.purple-light {
    li {
      background: rgba(@brand-primary, .2);
    }
  }
  &.purple, &.purple-solid, &.purple-underline {
    li {
      background: rgba(#bfacc5, .5);
    }
  }
  &.purple-dark {
    li {
      background: rgba(@brand-primary-dark, .2);
    }
  }
  &.red, &.red-solid, &.red-underline {
    li {
      background: rgba(#f1b4b5, .5);
    }
  }
  &.red-dark {
    li {
      background: rgba(@basic-red, .2);
    }
  }
  &.orange, &.orange-solid, &.orange-underline {
    li {
      background: rgba(#fbcdb5, .5);
    }
  }
  &.orange-dark {
    li {
      background: rgba(@brand-secondary-dark, .2);
    }
  }
  &.yellow, &.yellow-solid, &.yellow-underline {
    li {
      background: rgba(#feedba, .5);
    }
  }
  &.yellow-dark {
    li {
      background: rgba(@yellow-300, .2);
    }
  }
  &.grey {
    li {
      background: rgba(@bg-dark, .2);
    }
  }
  &.grey-solid {
    li {
      background: rgba(@bg-dark, .2);
    }
  }
}
.most-often {
  font-size: 14px;
  line-height: 16px;
  padding: 24px 0 0;
}
.most-often-results {
  font-weight: 500;
}
.points-per-row-wrap {
  display: flex;
  flex-direction: column;
}
.points-per-row {
  display: inline-flex;
  margin: 0 0 0 -4px;
  vertical-align: top;
  &.score-good {
    .point-per-row {
      > div {
        &:after {
          background: @basic-green;
        }
      }
    }
  }
  &.score-near-good {
    .point-per-row {
      > div {
        &:after {
          background: @blue-300;
        }
      }
    }
  }
  &.score-average {
    .point-per-row {
      > div {
        &:after {
          background: @brand-primary;
        }
      }
    }
  }
  &.score-poor {
    .point-per-row {
      > div {
        &:after {
          background: @basic-red;
        }
      }
    }
  }
  &.score-near-bad {
    .point-per-row {
      > div {
        &:after {
          background: @basic-red;
        }
      }
    }
  }
  &.score-bad {
    .point-per-row {
      > div {
        &:after {
          background: @basic-red;
        }
      }
    }
  }
  &.score-none {
    .point-per-row {
      > div {
        &:after {
          background: rgba(@grey-900, .5);
        }
      }
    }
  }

  &.green-light {
    .point-per-row {
      > div {
        &:after {
          background: @green-700;
        }
      }
    }
  }
  &.green {
    .point-per-row {
      > div {
        &:after {
          background: @basic-green;
        }
      }
    }
  }
  &.green-solid {
    .point-per-row {
      > div {
        &:after {
          background: @basic-green;
        }
      }
    }
  }
  &.green-dark {
    .point-per-row {
      > div {
        &:after {
          background: @basic-green;
        }
      }
    }
  }
  &.blue-light {
    .point-per-row {
      > div {
        &:after {
          background: @blue-300;
        }
      }
    }
  }
  &.blue {
    .point-per-row {
      > div {
        &:after {
          background: @brand-primary;
        }
      }
    }
  }
  &.blue-solid {
    .point-per-row {
      > div {
        &:after {
          background: @brand-primary;
        }
      }
    }
  }
  &.blue-dark {
    .point-per-row {
      > div {
        &:after {
          background: @navy-700;
        }
      }
    }
  }
  &.blue-extradark {
    .point-per-row {
      > div {
        &:after {
          background: @grey-900;
        }
      }
    }
  }
  &.purple-light {
    .point-per-row {
      > div {
        &:after {
          background: @brand-primary;
        }
      }
    }
  }
  &.purple {
    .point-per-row {
      > div {
        &:after {
          background: @brand-primary;
        }
      }
    }
  }
  &.purple-solid {
    .point-per-row {
      > div {
        &:after {
          background: @brand-primary;
        }
      }
    }
  }
  &.purple-dark {
    .point-per-row {
      > div {
        &:after {
          background: @brand-primary-dark;
        }
      }
    }
  }
  &.red {
    .point-per-row {
      > div {
        &:after {
          background: @basic-red;
        }
      }
    }
  }
  &.red-solid {
    .point-per-row {
      > div {
        &:after {
          background: @basic-red;
        }
      }
    }
  }
  &.red-dark {
    .point-per-row {
      > div {
        &:after {
          background: @basic-red;
        }
      }
    }
  }
  &.orange {
    .point-per-row {
      > div {
        &:after {
          background: @basic-orange;
        }
      }
    }
  }
  &.orange-solid {
    .point-per-row {
      > div {
        &:after {
          background: @basic-orange;
        }
      }
    }
  }
  &.orange-dark {
    .point-per-row {
      > div {
        &:after {
          background: @brand-secondary-dark;
        }
      }
    }
  }
  &.yellow {
    .point-per-row {
      > div {
        &:after {
          background: @basic-yellow;
        }
      }
    }
  }
  &.yellow-solid {
    .point-per-row {
      > div {
        &:after {
          background: #EEC770;
        }
      }
    }
  }
  &.yellow-dark {
    .point-per-row {
      > div {
        &:after {
          background: @yellow-300;
        }
      }
    }
  }
  &.grey {
    .point-per-row {
      > div {
        &:after {
          background: @bg-dark;
        }
      }
    }
  }
  &.grey-solid {
    .point-per-row {
      > div {
        &:after {
          background: @bg-dark;
        }
      }
    }
  }
}
.point-per-row {
  width: 16px;
  height: 8px;
  > div {
    width: 100%;
    height: 8px;
    overflow: hidden;
    position: relative;
    &:after {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      display: block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: rgba(@grey-900, .2) !important;
      margin: 0 4px;
    }
  }
}
.progress-animation-horizontal, .progress-animation-vertical {
  -webkit-transition: all 1s ease-in;
  transition: all 1s ease-in;
}

:deep(.v-skeleton-loader__bone) {
  margin: 0;
}

.flex-grow-5 {
  flex-grow: 5;
}

.flex-grow-7 {
  flex-grow: 7;
}
</style>
